// components/Publishers/PublisherPublications.jsx  (senin dosya ismine uyarlayabilirsin)
import React, { useEffect, useState, useContext } from "react";
import { Link, useParams } from "react-router-dom";
import { buildAuthorPath, buildPublicationPath, buildPublisherPath } from "../../../utils/slugify";
import Header2 from "../../header2/index-two";
import InstructorSidebar from "../sidebar";
import { AppContext } from "../../../services/AuthContext";
import { ListByPublisherIdsearch } from "../../../services/publicationService";
import Spinner from "react-bootstrap/Spinner";

const norm = (s) =>
  (s ?? "").toString().toLowerCase().normalize("NFKD").trim();

// Üst Yayın bilgisini (Dergi/Kitap) parentList'ten çeken fonksiyon
const extractParentInfo = (pub) => {
  if (!Array.isArray(pub?.parentList) || pub.parentList.length === 0) return { name: "-", id: null };

  // parentList içinde "Dergi" veya "Kitap" olan asıl üst yapıyı bulmaya çalış
  const rootParent = pub.parentList.find(p => {
    const t = extractType(p);
    return norm(t) === "dergi" || norm(t) === "kitap";
  }) || pub.parentList[pub.parentList.length - 1]; // Bulamazsa listenin en sonundakini al

  return {
    name: extractTitle(rootParent),
    id: rootParent?.mysqlId || null
  };
};

const extractAuthor = (pub) => {
  if (!Array.isArray(pub?.authors) || !pub.authors.length) return "-";
  const a = pub.authors[0]; // ilk yazar
  return `${a.name || ""} ${a.surname || ""}`.trim();
};

const extractYear = (pub) => {
  const dateStr = extractPubDate(pub);
  if (!dateStr) return "";
  return dateStr.slice(0, 4); // YYYY kısmını al
};

const pickBlockItem = (blocks, names) => {
  if (!Array.isArray(blocks)) return null;
  const want = (Array.isArray(names) ? names : [names]).map(norm);
  return blocks.find((it) => want.includes(norm(it?.tag ?? it?.attributename))) || null;
};

const pickLangFirst = (item, lang = "tr") => {
  const arr = item?.value ?? [];
  const row = arr.find((v) => norm(v?.lang) === norm(lang)) || arr[0];
  return Array.isArray(row?.value) ? row.value[0] ?? "" : "";
};

const toDateYYYYMMDD = (s) => {
  if (!s) return "";
  const m = String(s).match(/\d{4}-\d{2}-\d{2}/);
  if (m) return m[0];
  return String(s).split(" ")[0] || "";
};

const extractTitle = (pub) => {
  const item =
    pickBlockItem(pub?.templateInfo, "başlık") ||
    pickBlockItem(pub?.publicationtypeInfo, "başlık") ||
    pickBlockItem(pub?.templateInfo, "dergi adı") ||
    pickBlockItem(pub?.publicationtypeInfo, "dergi adı");
  return pickLangFirst(item) || "-";
};

const extractType = (pub) => {
  if (pub?.publicationtype) return pub.publicationtype;
  const item =
    pickBlockItem(pub?.publicationtypeInfo, "yayın türü") ||
    pickBlockItem(pub?.templateInfo, "yayın türü");
  return pickLangFirst(item) || "";
};

const extractPubDate = (pub) => {
  const item =
    pickBlockItem(pub?.publicationtypeInfo, "yayın tarihi") ||
    pickBlockItem(pub?.templateInfo, "yayın tarihi");
  return toDateYYYYMMDD(pickLangFirst(item));
};

const cleanLabel = (text) => {
  if (!text || text === "-") return text;
  return text
    .replace(/Akademik/gi, "")
    .replace(/Popüler/gi, "")
    .replace(/\s+/g, " ") // Kelime silindikten sonra kalan çift boşlukları teke indirir
    .trim();
};

const PublisherPublications = () => {
  const { auth } = useContext(AppContext);
  const { id } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0); // API 0-tabanlı sayfa bekliyor
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await ListByPublisherIdsearch(id, 1, currentPage, pageSize, auth.token);

        if (res?.status === "OK" && Array.isArray(res?.data)) {
          const parsed = res.data.map(({ elasticjson: pub }) => {
            // Üst yayın bilgisini burada çekiyoruz
            const parent = extractParentInfo(pub);
            return {
              id: pub.mysqlId,
              title: extractTitle(pub),
              author: extractAuthor(pub),
              authorId: pub.authors?.[0]?.mysqlId || null,
              type: cleanLabel(extractType(pub)),
              date: extractYear(pub),
              // Yeni alanlar
              parentTitle: cleanLabel(parent.name),
              parentMysqlId: parent.id
            };
          });
          setRows(parsed);
          setTotalCount(res.count || 0);
        } else {
          setRows([]);
          setTotalCount(0);
        }
      } catch (e) {
        console.error(e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
  }, [id, auth?.token, currentPage]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="main-wrapper mt-5">
      <Header2 />
      <div className="page-content text-sm">
        <div className="container">
          <div className="row">
            <InstructorSidebar />
            <div className="col-xl-9 col-lg-9">
              <div className="settings-widget card-details">
                <div className="settings-menu p-0">
                  <div className="profile-heading d-flex justify-content-between align-items-center">
                    <h3>Yayınlar</h3>
                    <span className="text-muted small">Toplam: {totalCount} kayıt</span>
                  </div>
                  <div className="instructor-course-table">
                    <div className="table-responsive custom-table">
                      <table className="table mb-0">
                        <thead>
                          <tr>
                            <th>Başlık</th>
                            <th>Yazar</th>
                            <th style={{ width: 150 }}>Tür</th>
                            <th>Üst Yayın</th> {/* Yeni Sütun */}
                            <th style={{ width: 100 }}>Yayın Yılı</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="5" className="text-center py-5">
                                <Spinner animation="border" size="sm" variant="primary" />
                              </td>
                            </tr>
                          ) : rows.length ? (
                            rows.map((p) => (
                              <tr key={p.id}>
                                <td>
                                  <Link to={buildPublicationPath({ title: p.title, id: p.id })}>{p.title}</Link>
                                </td>
                                <td>
                                  {p.authorId ? (
                                    <Link to={buildAuthorPath({ fullName: p.author, id: p.authorId })}>{p.author}</Link>
                                  ) : (
                                    p.author
                                  )}
                                </td>
                                <td>{p.type}</td>
                                <td>
                                  {p.parentMysqlId ? (
                                    <Link to={buildPublicationPath({ title: p.parentTitle, id: p.parentMysqlId })}>
                                      {p.parentTitle}
                                    </Link>
                                  ) : (
                                    p.parentTitle
                                  )}
                                </td>
                                <td>{p.date}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">Yayın bulunamadı.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination UI */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center pb-4 mt-3">
                      <nav aria-label="Page navigation">
                        <ul className="pagination pagination-sm mb-0 d-flex gap-2">

                          {/* İlk Sayfa */}
                          <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setCurrentPage(0)}
                              disabled={currentPage === 0}
                              title="İlk Sayfa"
                            >
                              <i className="fas fa-angle-double-left"></i> İlk
                            </button>
                          </li>

                          {/* Önceki Sayfa */}
                          <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setCurrentPage(prev => prev - 1)}
                              disabled={currentPage === 0}
                            >
                              Önceki
                            </button>
                          </li>

                          {/* Sayfa Bilgisi */}
                          <li className="page-item">
                            <span className="btn btn-sm btn-primary disabled text-dark fw-bold">
                              {currentPage + 1} / {totalPages}
                            </span>
                          </li>

                          {/* Sonraki Sayfa */}
                          <li className={`page-item ${currentPage + 1 >= totalPages ? "disabled" : ""}`}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setCurrentPage(prev => prev + 1)}
                              disabled={currentPage + 1 >= totalPages}
                            >
                              Sonraki
                            </button>
                          </li>

                          {/* Son Sayfa */}
                          <li className={`page-item ${currentPage + 1 >= totalPages ? "disabled" : ""}`}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setCurrentPage(totalPages - 1)}
                              disabled={currentPage + 1 >= totalPages}
                              title="Son Sayfa"
                            >
                              Son <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>

                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublisherPublications;
